import React from 'react';
import { cn } from '@/lib/utils';

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Base skeleton component with shimmer animation
 * Used as building block for specific skeleton variants
 */
export const Skeleton: React.FC<SkeletonProps> = ({ className, style }) => {
  return (
    <div
      className={cn(
        'animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]',
        className
      )}
      style={style}
    />
  );
};

/**
 * Video thumbnail skeleton that matches SimpleVideoThumbnail layout
 * Preserves 16:9 aspect ratio and badge placement
 */
export const VideoSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div
      className={cn(
        'rounded-xl overflow-hidden shadow-lg relative bg-gray-200',
        className
      )}
      style={{ aspectRatio: '16/9' }}
    >
      {/* Shimmer overlay */}
      <div className="absolute inset-0 skeleton-shimmer" />
      
      {/* Top badges skeleton */}
      <div className="absolute top-0 left-0 right-0 flex items-start justify-between gap-2 p-[10px] z-10">
        {/* Model badge skeleton */}
        <Skeleton
          className="rounded-[30px]"
          style={{
            height: '30px',
            minWidth: '140px',
          }}
        />
        
        {/* Status badge skeleton */}
        <Skeleton
          className="rounded-[30px]"
          style={{
            width: '90px',
            height: '30px',
          }}
        />
        
        {/* Email badge skeleton */}
        <Skeleton
          className="rounded-[30px]"
          style={{
            width: '130px',
            height: '30px',
          }}
        />
      </div>
    </div>
  );
};

/**
 * Image skeleton that matches the 1:1 aspect ratio image tiles
 */
export const ImageSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div
      className={cn(
        'rounded-xl overflow-hidden shadow-lg relative bg-gray-200',
        className
      )}
      style={{ aspectRatio: '1/1' }}
    >
      {/* Shimmer overlay */}
      <div className="absolute inset-0 skeleton-shimmer" />
      
      {/* Top badges skeleton - matching image overlay layout */}
      <div className="absolute top-0 left-0 right-0 flex items-start justify-between gap-2 p-[10px] z-10">
        {/* Model badge skeleton */}
        <Skeleton
          className="rounded-[30px]"
          style={{
            height: '30px',
            minWidth: '100px',
            flex: '1 1 auto',
            maxWidth: '20%',
          }}
        />
        
        {/* Status badge skeleton */}
        <Skeleton
          className="rounded-[30px]"
          style={{
            width: '90px',
            height: '30px',
          }}
        />
        
        {/* Email badge skeleton */}
        <Skeleton
          className="rounded-[30px]"
          style={{
            width: '130px',
            height: '30px',
          }}
        />
      </div>
    </div>
  );
};

/**
 * Transcription card skeleton
 */
export const TranscriptionSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div
      className={cn(
        'rounded-xl overflow-hidden shadow-lg p-4 bg-gray-100',
        className
      )}
    >
      {/* Title skeleton */}
      <Skeleton className="h-5 w-3/4 rounded mb-3" />
      
      {/* Word count skeleton */}
      <Skeleton className="h-4 w-1/2 rounded mb-2" />
      
      {/* Date skeleton */}
      <Skeleton className="h-4 w-1/3 rounded" />
    </div>
  );
};

/**
 * Skeleton grid for initial loading state
 * Shows a grid of skeleton items matching the actual grid layout
 */
interface SkeletonGridProps {
  count?: number;
  type: 'video' | 'image' | 'transcription';
  className?: string;
}

export const SkeletonGrid: React.FC<SkeletonGridProps> = ({
  count = 12,
  type,
  className,
}) => {
  const SkeletonComponent = {
    video: VideoSkeleton,
    image: ImageSkeleton,
    transcription: TranscriptionSkeleton,
  }[type];

  const gridClasses = type === 'image'
    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';

  return (
    <div className={cn(gridClasses, className)}>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonComponent key={`skeleton-grid-${type}-${index}-of-${count}`} />
      ))}
    </div>
  );
};

/**
 * Video row skeleton for virtualized grids
 * Renders a row of video skeleton cards
 */
interface VideoRowSkeletonProps {
  /** Number of columns in the row */
  readonly columns: number;
  /** Optional row index for unique keys */
  readonly rowIndex?: number;
}

export const VideoRowSkeleton: React.FC<VideoRowSkeletonProps> = ({ columns, rowIndex = 0 }) => (
  <div
    className="grid gap-4"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
  >
    {Array.from({ length: columns }).map((_, i) => (
      <VideoSkeleton key={`video-row-skeleton-${rowIndex}-col-${i}`} />
    ))}
  </div>
);

/**
 * Image row skeleton for virtualized grids
 * Renders a row of image skeleton cards
 */
interface ImageRowSkeletonProps {
  /** Number of columns in the row */
  readonly columns: number;
  /** Optional row index for unique keys */
  readonly rowIndex?: number;
}

export const ImageRowSkeleton: React.FC<ImageRowSkeletonProps> = ({ columns, rowIndex = 0 }) => (
  <div
    className="grid gap-4"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
  >
    {Array.from({ length: columns }).map((_, i) => (
      <ImageSkeleton key={`image-row-skeleton-${rowIndex}-col-${i}`} />
    ))}
  </div>
);

export default Skeleton;
